import { nonEmptyTrimmedStringFromUnknown } from "../protocol/shared.js";
import { runtimeCoordinatorCancellationStateSchema, runtimeCoordinatorExecutionRecordSchema } from "../daemon/runtime-coordinator-execution.js";
import { clientMessageIdSchema } from "../protocol/client-message-id.js";
import * as z$1 from "zod/v4";
//#region src/schemas/http/space-wake-stop.ts
const boundedReasonCodeSchema = z$1.string().trim().min(1).max(160);
const boundedSelectorIdSchema = z$1.string().trim().min(1).max(512);
const wakeStopReasonSchema = z$1.string().trim().min(1).max(512);
const spaceWakeStopSelectorSchema = z$1.discriminatedUnion("by", [z$1.strictObject({
	by: z$1.literal("source_signal"),
	sourceSignalId: boundedSelectorIdSchema,
	targetProfileId: boundedSelectorIdSchema
}), z$1.strictObject({
	by: z$1.literal("client_message"),
	clientMessageId: clientMessageIdSchema,
	targetProfileId: boundedSelectorIdSchema
})]);
const postSpaceWakeStopBodySchema = z$1.strictObject({
	reason: wakeStopReasonSchema.optional(),
	target: spaceWakeStopSelectorSchema
});
const postSpaceWakeStopInputSchema = z$1.strictObject({
	body: postSpaceWakeStopBodySchema,
	spaceId: nonEmptyTrimmedStringFromUnknown
});
const spaceWakeStopResponseBaseSchema = z$1.strictObject({ targetProfileId: boundedSelectorIdSchema });
const wakeStopExecutionIdSchema = runtimeCoordinatorExecutionRecordSchema.shape.runtimeCoordinatorExecutionId;
const wakeStopModeSchema = z$1.enum([
	"openmeld_owned_pre_agent",
	"controller_control",
	"remote_agent_native_delivery"
]);
const wakeStopRequestPendingCancellationStateSchema = runtimeCoordinatorCancellationStateSchema.extend({ status: z$1.enum(["requested", "delivery_pending"]) });
const wakeStopCancelledCancellationStateSchema = runtimeCoordinatorCancellationStateSchema.extend({ status: z$1.literal("cancelled") });
const wakeStopNotRequestedCancellationStateSchema = runtimeCoordinatorCancellationStateSchema.extend({ status: z$1.literal("not_requested") });
const wakeStopNotStoppableCancellationStateSchema = runtimeCoordinatorCancellationStateSchema.extend({ status: z$1.enum(["not_requested", "local_control_rejected"]) });
const wakeStopUnsupportedCancellationStateSchema = runtimeCoordinatorCancellationStateSchema.extend({ status: z$1.literal("unsupported") });
const wakeStopRetryLaterCancellationStateSchema = runtimeCoordinatorCancellationStateSchema.extend({ status: z$1.literal("delivery_rejected") });
const wakeStopSuccessResponseBaseSchema = spaceWakeStopResponseBaseSchema.extend({
	ok: z$1.literal(true),
	runtimeCoordinatorExecutionId: wakeStopExecutionIdSchema.nullable(),
	stopMode: wakeStopModeSchema.optional()
});
const wakeStopFailureResponseBaseSchema = spaceWakeStopResponseBaseSchema.extend({
	ok: z$1.literal(false),
	reasonCode: boundedReasonCodeSchema,
	runtimeCoordinatorExecutionId: wakeStopExecutionIdSchema.nullable()
});
const postSpaceWakeStopResponseSchema = z$1.discriminatedUnion("status", [
	wakeStopSuccessResponseBaseSchema.extend({
		cancellationState: wakeStopRequestPendingCancellationStateSchema,
		status: z$1.literal("cancellation_requested")
	}),
	wakeStopSuccessResponseBaseSchema.extend({
		cancellationState: wakeStopCancelledCancellationStateSchema,
		status: z$1.literal("cancelled")
	}),
	wakeStopSuccessResponseBaseSchema.extend({
		cancellationState: wakeStopCancelledCancellationStateSchema,
		status: z$1.literal("already_cancelled")
	}),
	wakeStopFailureResponseBaseSchema.extend({
		cancellationState: wakeStopNotRequestedCancellationStateSchema,
		status: z$1.literal("not_found")
	}),
	wakeStopFailureResponseBaseSchema.extend({
		cancellationState: wakeStopNotStoppableCancellationStateSchema,
		status: z$1.literal("not_stoppable")
	}),
	wakeStopFailureResponseBaseSchema.extend({
		cancellationState: wakeStopUnsupportedCancellationStateSchema,
		status: z$1.literal("unsupported")
	}),
	wakeStopFailureResponseBaseSchema.extend({
		cancellationState: wakeStopRetryLaterCancellationStateSchema,
		status: z$1.literal("retry_later")
	})
]).superRefine((response, context) => {
	if (!response.ok) return;
	if (response.status === "cancellation_requested") {
		if (response.runtimeCoordinatorExecutionId === null && response.stopMode !== "remote_agent_native_delivery") context.addIssue({
			code: "custom",
			message: "controller-backed cancellation requests require a RuntimeCoordinator execution id",
			path: ["runtimeCoordinatorExecutionId"]
		});
		if (response.stopMode === "openmeld_owned_pre_agent") context.addIssue({
			code: "custom",
			message: "pre-agent Stop must be terminal cancelled, not cancellation_requested",
			path: ["stopMode"]
		});
		return;
	}
	if (response.runtimeCoordinatorExecutionId === null) {
		if (response.stopMode !== "openmeld_owned_pre_agent" && response.stopMode !== "remote_agent_native_delivery") context.addIssue({
			code: "custom",
			message: "Wake Stop success without an execution id must name its OpenMeld-owned stop mode",
			path: ["stopMode"]
		});
		return;
	}
	if (response.stopMode === "openmeld_owned_pre_agent") context.addIssue({
		code: "custom",
		message: "OpenMeld-owned pre-agent Stop must not carry a RuntimeCoordinator execution id",
		path: ["runtimeCoordinatorExecutionId"]
	});
});
//#endregion
export { postSpaceWakeStopBodySchema, postSpaceWakeStopInputSchema, postSpaceWakeStopResponseSchema, spaceWakeStopSelectorSchema };

//# sourceMappingURL=space-wake-stop.js.map